import {
  AssistantToolCall,
  ChatMessage,
  ChatStreamEvent,
} from './axios/api';

type ToolCallDelta = {
  index: number;
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string };
};

export class ChatMessagesBuilder {
  private messages: ChatMessage[] = [];
  private streamingAssistantId: string | null = null;
  private readonly pendingToolCalls = new Map<number, AssistantToolCall>();

  static empty(): ChatMessagesBuilder {
    return new ChatMessagesBuilder();
  }

  static from(messages: readonly ChatMessage[]): ChatMessagesBuilder {
    return new ChatMessagesBuilder().from(messages);
  }

  from(messages: readonly ChatMessage[]): this {
    this.messages = messages.map((message) => this.cloneMessage(message));
    this.resetStream();
    return this;
  }

  user(message: Pick<ChatMessage, 'id' | 'text' | 'files'>): this {
    this.resetStream();
    this.push({
      id: message.id,
      role: 'USER',
      text: message.text,
      files: message.files ?? [],
    });
    this.ensureAssistant();
    return this;
  }

  replaceMessageId(fromId: string, toId: string): this {
    const message = this.findMessage(fromId);

    if (message) {
      message.id = toId;
    }

    return this;
  }

  replaceFile(
    messageId: string,
    localFileId: string,
    file: NonNullable<ChatMessage['files']>[number],
  ): this {
    const message = this.findMessage(messageId);

    if (!message?.files) {
      return this;
    }

    message.files = message.files.map((item) =>
      item.id === localFileId ? { ...file, isUploading: false } : item,
    );

    return this;
  }

  apply(event: ChatStreamEvent): this {
    switch (event.type) {
      case 'thinking':
        return this.thinking(event.data);
      case 'text':
        return this.text(event.data);
      case 'tool_calls':
        return this.toolCalls(event.data);
      case 'tool_result':
        return this.toolResult(event.data);
      case 'tool_approval_required':
        return this.syncToolMessages(event.data);
      case 'done':
        return this.done(event.data);
      case 'error':
        return this.error(event.data.message);
      case 'chat_name':
        return this;
    }
  }

  thinking(delta: string): this {
    const assistant = this.ensureAssistant();
    assistant.thinking = `${assistant.thinking ?? ''}${delta}`;
    return this;
  }

  text(delta: string): this {
    this.ensureAssistant().text += delta;
    return this;
  }

  toolCalls(data: unknown): this {
    this.mergeToolCallDeltas(this.asToolCallDeltas(data));

    Array.from(this.pendingToolCalls.values()).forEach((toolCall) => {
      if (!toolCall.id) {
        return;
      }

      this.upsertToolMessage(toolCall);
    });

    return this;
  }

  toolResult(data: { id: string; name: string; result: string }): this {
    this.resetStream();

    const message =
      this.findMessage(this.toolMessageId(data.id)) ??
      this.messages.find(
        (item) => item.role === 'TOOL' && item.toolCalls?.id === data.id,
      );

    if (!message) {
      return this;
    }

    message.text = data.result;
    message.toolCalls = {
      id: data.id,
      type: 'function',
      function: {
        name: data.name,
        arguments: message.toolCalls?.function.arguments ?? '',
      },
    };

    return this;
  }

  syncToolMessages(messages: readonly ChatMessage[]): this {
    this.resetStream();

    messages.forEach((message) => {
      const existing =
        this.findMessage(message.id) ??
        (message.toolCalls
          ? this.findMessage(this.toolMessageId(message.toolCalls.id))
          : undefined);

      if (existing) {
        Object.assign(existing, this.cloneMessage(message));
        return;
      }

      this.push(this.cloneMessage(message));
    });

    return this;
  }

  done(message: ChatMessage): this {
    this.resetStream();

    const lastAssistant = this.findLast(
      (item) => item.role === 'ASSISTANT',
    );

    if (!lastAssistant) {
      return this.push(this.cloneMessage(message));
    }

    lastAssistant.id = message.id;
    lastAssistant.text = message.text || lastAssistant.text;
    lastAssistant.thinking = message.thinking ?? lastAssistant.thinking;

    return this;
  }

  error(message: string): this {
    this.ensureAssistant().text = message;
    return this;
  }

  build(): ChatMessage[] {
    return this.messages.map((message) => this.cloneMessage(message));
  }

  private push(message: ChatMessage): this {
    this.messages.push(message);
    return this;
  }

  private resetStream(): void {
    this.streamingAssistantId = null;
    this.pendingToolCalls.clear();
  }

  private ensureAssistant(): ChatMessage {
    if (this.streamingAssistantId) {
      const current = this.findMessage(this.streamingAssistantId);

      if (current) {
        return current;
      }
    }

    const assistant: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'ASSISTANT',
      text: '',
      thinking: '',
    };

    this.streamingAssistantId = assistant.id;
    this.messages.push(assistant);

    return assistant;
  }

  private upsertToolMessage(toolCall: AssistantToolCall): void {
    const id = this.toolMessageId(toolCall.id);
    const existing = this.findMessage(id);
    const toolMessage: ChatMessage = {
      id,
      role: 'TOOL',
      text: existing?.text ?? '',
      toolCalls: {
        id: toolCall.id,
        type: 'function',
        function: {
          name: toolCall.function.name,
          arguments: toolCall.function.arguments,
        },
      },
    };

    if (existing) {
      Object.assign(existing, toolMessage);
      return;
    }

    this.messages.push(toolMessage);
  }

  private mergeToolCallDeltas(deltas: ToolCallDelta[]): void {
    deltas.forEach((delta) => {
      const current = this.pendingToolCalls.get(delta.index) ?? {
        id: '',
        type: 'function' as const,
        function: {
          name: '',
          arguments: '',
        },
      };

      if (delta.id) {
        current.id = delta.id;
      }

      if (delta.function?.name) {
        current.function.name += delta.function.name;
      }

      if (delta.function?.arguments) {
        current.function.arguments += delta.function.arguments;
      }

      this.pendingToolCalls.set(delta.index, current);
    });
  }

  private asToolCallDeltas(data: unknown): ToolCallDelta[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data as ToolCallDelta[];
  }

  private toolMessageId(toolCallId: string): string {
    return `tool:${toolCallId}`;
  }

  private findMessage(id: string): ChatMessage | undefined {
    return this.messages.find((message) => message.id === id);
  }

  private findLast(
    predicate: (message: ChatMessage) => boolean,
  ): ChatMessage | undefined {
    for (let index = this.messages.length - 1; index >= 0; index -= 1) {
      if (predicate(this.messages[index])) {
        return this.messages[index];
      }
    }

    return undefined;
  }

  private cloneMessage(message: ChatMessage): ChatMessage {
    return {
      ...message,
      files: message.files ? message.files.map((file) => ({ ...file })) : message.files,
      toolCalls: message.toolCalls
        ? {
            ...message.toolCalls,
            function: { ...message.toolCalls.function },
          }
        : message.toolCalls,
    };
  }
}
